/* Entropica festive overlay.
   Glowing line art drifting over the black storefront: snow crystals, glass ornaments, coffee beans,
   the E mark, caffeine, and a reindeer that flies across every 14 to 20 seconds.

   Needs <canvas id="ec-festive" data-density="light|medium|heavy" data-layer="behind|over"
   data-until="YYYY-MM-DD" data-phone-unpin="true|false">.
   In the theme, sections/festive-overlay.liquid renders it; the vault prototype inlines this file.
   Source of truth: 99 Meta/Theme/festive-overlay/festive-overlay.js in the vault. */
(() => {
  'use strict';

  if (window.ECFestive) return; // loaded twice: the first copy already handles the page and the editor

  let current = null;

  function mountFrom(scope) {
    const canvas = (scope || document).querySelector('canvas#ec-festive:not([data-ec-mounted])');
    if (!canvas || !canvas.getContext) return;
    if (current) current.destroy();
    current = mount(canvas);
  }

  // Theme editor: the section can be re-rendered or removed without a page load.
  document.addEventListener('shopify:section:unload', e => {
    if (current && e.detail && current.sectionId === e.detail.sectionId) { current.destroy(); current = null; }
  });
  document.addEventListener('shopify:section:load', e => mountFrom(e.target));

  window.ECFestive = { get current() { return current; } };
  mountFrom(document);

  function mount(canvas) {
    canvas.dataset.ecMounted = '';
    const sectionEl = canvas.closest('.shopify-section');
    const sectionId = sectionEl ? sectionEl.id.replace('shopify-section-', '') : null;

    // Off after the last day set in the theme editor, in the shopper's own time zone.
    // A value that isn't YYYY-MM-DD also counts as off, so a typo can't leave it running forever.
    const until = (canvas.dataset.until || '').trim();
    const now = new Date();
    const today = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
    if (until && (!/^\d{4}-\d{2}-\d{2}$/.test(until) || today > until)) {
      canvas.remove();
      return { sectionId, destroy() {} };
    }

    const ctx = canvas.getContext('2d');
    const root = document.documentElement;
    const DENSITY_K = { light: 0.025, medium: 0.043, heavy: 0.07 };
    const cfg = {
      density: DENSITY_K[canvas.dataset.density] ? canvas.dataset.density : 'light',
      layer: canvas.dataset.layer === 'over' ? 'over' : 'behind'
    };

    // The brand glow, after the Aromatic Matrix trace, feathered: a tight halo and a wide soft one,
    // painted under a crisp line so the line itself stays sharp. The reindeer keeps its own single glow.
    const HALO = [['rgba(255, 255, 255, 0.5)', 2], ['rgba(255, 255, 255, 0.3)', 8]];
    const HALO_PAD = 14;
    const DEER_GLOW = 'rgba(255, 255, 255, 0.6)', DEER_GLOW_BLUR = 6;
    const TAU = Math.PI * 2, D2R = Math.PI / 180;
    const R = Math.random;
    const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
    const lerp = (a, b, t) => a + (b - a) * t;

    // "Behind" sits just above the page's base black. The page wrappers that paint their own black
    // go transparent while the layer runs, and product cards (solid #050505) drop to a 45% fill,
    // so the art shows through without changing how the page looks.
    // On phones the product page pins its photo and slides a solid black details panel over it;
    // with data-phone-unpin the photo scrolls normally instead and the panel goes clear, so the art shows there too.
    const style = document.createElement('style');
    style.id = 'ec-festive-style';
    style.textContent =
      '#ec-festive{position:fixed;inset:0;width:100%;height:100%;pointer-events:none}' +
      'html.ec-festive-on{background-color:#000}' +
      'html.ec-festive-behind body,html.ec-festive-behind .entropica-product-master,' +
      'html.ec-festive-behind .entropica-product-grid-master,html.ec-festive-behind .entropica-footer' +
      '{background-color:transparent!important}' +
      'html.ec-festive-behind .card--card{background-color:rgba(5,5,5,0.45)!important}' +
      '@media screen and (max-width:900px){' +
      'html.ec-festive-behind.ec-festive-unpin .product-gallery-pane{position:relative!important;top:auto!important}' +
      'html.ec-festive-behind.ec-festive-unpin .product-data-pane{background-color:transparent!important}' +
      'html.ec-festive-behind.ec-festive-unpin .product-data-pane::before{display:none!important}}';
    document.head.appendChild(style);
    document.body.appendChild(canvas); // out of the section wrapper, so no ancestor can trap the fixed layer
    root.classList.add('ec-festive-on');
    root.classList.toggle('ec-festive-unpin', canvas.dataset.phoneUnpin === 'true');

    // Set inline: the section's inline style already carries z-index, and inline beats any stylesheet rule.
    // "Over" stays under the header (90000) and cart drawer (89000).
    function applyLayer(layer) {
      cfg.layer = layer === 'over' ? 'over' : 'behind';
      canvas.dataset.layer = cfg.layer;
      canvas.style.zIndex = cfg.layer === 'over' ? '50' : '-1';
      root.classList.toggle('ec-festive-behind', cfg.layer === 'behind');
    }
    applyLayer(cfg.layer);

    function seeded(a) {
      return () => {
        let t = (a += 0x6D2B79F5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
    }

    let W = 0, H = 0, DPR = 1, lastW = 0, lastH = 0, spriteDPR = 0, spriteSmall = null, S = null;
    let parts = [];
    const sparks = [];
    let clock = 0, last = 0, lastFrame = 0, raf = 0, frames = 0, booted = false, dead = false;

    // ---------- sprites ----------
    // The halo trick below draws a shape off to the side and throws only its shadow back into place.
    // If a browser skips shadows of off-canvas shapes, fall back to drawing the shape with its shadow in place.
    const OFFSET_SHADOW = (() => {
      try {
        const t = document.createElement('canvas');
        t.width = t.height = 8;
        const g = t.getContext('2d');
        g.translate(-1000, 0);
        g.shadowColor = '#fff';
        g.shadowOffsetX = 1000;
        g.fillStyle = '#fff';
        g.fillRect(2, 2, 4, 4);
        return g.getImageData(4, 4, 1, 1).data[3] > 0;
      } catch (e) {
        return false;
      }
    })();

    function makeSprite(w, h, ox, oy, paint) {
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.ceil(w * DPR));
      c.height = Math.max(1, Math.ceil(h * DPR));
      const g = c.getContext('2d');
      // Origin on the device-pixel grid, and the drawn size equal to the bitmap, so sprites copy 1:1 without resampling.
      ox = Math.round(ox * DPR) / DPR;
      oy = Math.round(oy * DPR) / DPR;
      g.setTransform(DPR, 0, 0, DPR, ox * DPR, oy * DPR);
      paint(g);
      return { c, w: c.width / DPR, h: c.height / DPR, ox, oy };
    }

    // Halo passes first, then the shape itself once on top, crisp.
    function inked(g, paint) {
      for (const [color, blur] of HALO) {
        g.save();
        g.globalAlpha = 1;
        if (OFFSET_SHADOW) {
          g.translate(-1000, 0);
          g.shadowOffsetX = 1000 * DPR;
        }
        g.shadowColor = color;
        g.shadowBlur = blur * DPR;
        paint(g);
        g.restore();
      }
      g.globalAlpha = 1;
      paint(g);
    }

    function softDot(size, inner) {
      const r = size / 2;
      return makeSprite(size, size, r, r, g => {
        const gr = g.createRadialGradient(0, 0, 0, 0, 0, r);
        gr.addColorStop(0, 'rgba(255,255,255,1)');
        gr.addColorStop(inner, 'rgba(255,255,255,0.6)');
        gr.addColorStop(1, 'rgba(255,255,255,0)');
        g.fillStyle = gr;
        g.beginPath(); g.arc(0, 0, r, 0, TAU); g.fill();
      });
    }

    // A six-fold dendrite: every arm carries the same side branches, like a real snow crystal.
    function crystal(size, seed) {
      const rnd = seeded(seed);
      const half = size / 2, pad = 2 + HALO_PAD, Rr = half;
      const nb = size < 14 ? 1 : 2 + Math.floor(rnd() * 2);
      const br = [];
      for (let i = 0; i < nb; i++) {
        const t = 0.32 + (i + 0.2 + rnd() * 0.6) * (0.56 / nb);
        br.push([t, Rr * (0.16 + rnd() * 0.2) * (1.15 - t * 0.6)]);
      }
      const plate = rnd() < 0.4;
      return makeSprite(size + pad * 2, size + pad * 2, half + pad, half + pad, g => inked(g, g => {
        g.strokeStyle = '#fff'; g.lineWidth = 1; g.lineCap = 'round'; g.lineJoin = 'round';
        g.beginPath();
        for (let a = 0; a < 6; a++) {
          const ang = (a * Math.PI) / 3, cx = Math.cos(ang), sy = Math.sin(ang);
          g.moveTo(0, 0); g.lineTo(cx * Rr, sy * Rr);
          for (const [t, len] of br) {
            const bx = cx * Rr * t, by = sy * Rr * t;
            for (const s of [-1, 1]) {
              const ba = ang + (s * Math.PI) / 3;
              g.moveTo(bx, by); g.lineTo(bx + Math.cos(ba) * len, by + Math.sin(ba) * len);
            }
          }
        }
        g.stroke();
        if (plate) {
          const hr = Rr * 0.22;
          g.beginPath();
          for (let k = 0; k < 6; k++) {
            const ang = (k * Math.PI) / 3;
            k ? g.lineTo(Math.cos(ang) * hr, Math.sin(ang) * hr) : g.moveTo(hr, 0);
          }
          g.closePath(); g.stroke();
        }
      }));
    }

    function shapePath(g, shape, cy, r) {
      if (shape === 'drop') {
        g.moveTo(0, cy - r);
        g.bezierCurveTo(r * 1.3, cy - r, r * 1.1, cy + r * 0.55, 0, cy + r * 1.55);
        g.bezierCurveTo(-r * 1.1, cy + r * 0.55, -r * 1.3, cy - r, 0, cy - r);
        g.closePath();
      } else {
        g.arc(0, cy, r, 0, TAU);
      }
    }

    // Glass ornament. The sprite's origin is the top of its thread, so it swings from there.
    function glassOrnament(r, shape) {
      const th = r * 1.3, loopR = 1.5, capW = r * 0.5, capH = r * 0.26;
      const capY = th + loopR * 2 - 0.4;
      const cy = capY + capH + r - r * 0.1;
      const bottom = cy + (shape === 'drop' ? 1.55 : 1) * r;
      const gp = HALO_PAD;
      const w = r * 2.7 + 4 + gp * 2, h = bottom + 3 + gp * 2;
      return makeSprite(w, h, w / 2, 1 + gp, g => inked(g, g => {
        g.strokeStyle = '#fff'; g.lineWidth = 1; g.lineCap = 'round';
        g.globalAlpha = 0.45;
        g.beginPath(); g.moveTo(0, 0); g.lineTo(0, th); g.stroke();
        g.globalAlpha = 1;
        g.beginPath(); g.arc(0, th + loopR, loopR, 0, TAU); g.stroke();
        g.strokeRect(-capW / 2, capY, capW, capH);
        g.beginPath(); shapePath(g, shape === 'drop' ? 'drop' : 'ball', cy, r); g.stroke();
        g.globalAlpha = 0.55;
        if (shape === 'meridian') {
          g.beginPath(); g.ellipse(0, cy, r * 0.42, r, 0, 0, TAU); g.stroke();
        } else if (shape === 'band') {
          for (const dy of [-0.3, 0.3]) {
            g.beginPath(); g.ellipse(0, cy + dy * r, r * 0.954, r * 0.22, 0, 0, Math.PI); g.stroke();
          }
        } else if (shape === 'drop') {
          g.beginPath(); g.moveTo(0, cy - r * 0.55); g.lineTo(0, cy + r * 1.15); g.stroke();
        }
        g.globalAlpha = 0.6;
        g.beginPath(); g.arc(0, cy, r * 0.72, Math.PI * 1.1, Math.PI * 1.4); g.stroke();
      }));
    }

    // Coffee bean, flat side up: a slightly lopsided oval whose lower edge dips in a touch,
    // with the S-shaped center cut. Each variant gets its own small irregularities.
    function bean(len, seed) {
      const rnd = seeded(seed);
      const rx = len / 2, ry = len * 0.36, pad = 2 + HALO_PAD;
      // Lower edge: y = sin(a) - c*sin(a)^4, which flattens the middle into a broad, shallow concave (3-6%).
      const c = 0.34 + rnd() * 0.1;
      const tilt = (rnd() - 0.5) * 0.12, blunt = 0.92 + rnd() * 0.06;
      const wob = 0.03 * rnd(), wph = rnd() * TAU;
      const outline = [];
      for (let i = 0; i < 48; i++) {
        const a = (i / 48) * TAU, ca = Math.cos(a), sa = Math.sin(a);
        const r = 1 + wob * Math.sin(3 * a + wph);
        outline.push([
          ca * rx * r * (ca < 0 ? blunt : 1),
          (sa > 0 ? sa - c * sa ** 4 : sa * (1 + tilt * ca)) * ry * r
        ]);
      }
      const k = 0.1 + rnd() * 0.06;
      const w = len + pad * 2, h = ry * 2 + pad * 2;
      return makeSprite(w, h, w / 2, h / 2, g => inked(g, g => {
        g.strokeStyle = '#fff'; g.lineWidth = 1; g.lineCap = 'round'; g.lineJoin = 'round';
        g.beginPath();
        outline.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y)));
        g.closePath(); g.stroke();
        g.beginPath(); g.moveTo(-rx * 0.8, -ry * 0.04);
        g.bezierCurveTo(-rx * 0.3, -len * k, rx * 0.3, len * k * 0.9, rx * 0.8, ry * 0.02);
        g.stroke();
      }));
    }

    function poly(g, pts) {
      g.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i][0], pts[i][1]);
      g.closePath();
    }

    // The favicon's E mark in outline, hung on a thread like the glass ornaments.
    // Traced from the 512px touch icon: 149 x 222 units, twin bars above and below,
    // and a middle arm split by a slanted cut.
    function markOrnament(height) {
      const s = height / 222, th = 9, loopR = 1.5, top = th + loopR * 2 + 1;
      const gp = HALO_PAD;
      const w = 149 * s + 4 + gp * 2, h = top + height + 3 + gp * 2;
      return makeSprite(w, h, w / 2, 1 + gp, g => inked(g, g => {
        g.strokeStyle = '#fff'; g.lineWidth = 1; g.lineCap = 'round'; g.lineJoin = 'miter';
        g.globalAlpha = 0.45;
        g.beginPath(); g.moveTo(0, 0); g.lineTo(0, th); g.stroke();
        g.globalAlpha = 1;
        g.beginPath(); g.arc(0, th + loopR, loopR, 0, TAU); g.stroke();
        g.save();
        g.translate((-149 * s) / 2, top);
        g.scale(s, s);
        g.lineWidth = 1 / s;
        g.beginPath();
        g.rect(0, 0, 149, 12);
        poly(g, [[0, 24], [149, 24], [149, 36], [24, 36], [24, 99], [54, 99], [68, 123], [24, 123], [24, 186], [149, 186], [149, 198], [0, 198]]);
        poly(g, [[69, 99], [105, 99], [105, 123], [83, 123]]);
        g.rect(0, 210, 149, 12);
        g.stroke();
        g.restore();
      }));
    }

    // Caffeine, 1,3,7-trimethylxanthine (C8H10N4O2), as a skeletal formula. SMILES: CN1C=NC2=C1C(=O)N(C(=O)N2C)C
    // Purine numbering: six-ring N1 C2 N3 C4 C5 C6, fused at C4-C5 to the five-ring C4 C5 N7 C8 N9.
    // C2=O and C6=O; methyls on N1, N3 and N7 drawn as bare line ends; C4=C5 and C8=N9 double; the H on C8 is implied.
    // Coordinates are in bond lengths: a regular hexagon and a regular pentagon sharing the C4-C5 edge.
    const CAF = {
      A: {
        N1: [-0.866, -0.5], C2: [-0.866, 0.5], N3: [0, 1], C4: [0.866, 0.5], C5: [0.866, -0.5], C6: [0, -1],
        N7: [1.817, -0.809], C8: [2.405, 0], N9: [1.817, 0.809],
        O2: [-1.732, 1], O6: [0, -2], M1: [-1.732, -1], M3: [0, 2], M7: [2.126, -1.76]
      },
      bonds: [
        ['N1', 'C2', 1], ['C2', 'N3', 1], ['N3', 'C4', 1], ['C4', 'C5', 2, 'hex'], ['C5', 'C6', 1], ['C6', 'N1', 1],
        ['C5', 'N7', 1], ['N7', 'C8', 1], ['C8', 'N9', 2, 'pent'], ['N9', 'C4', 1],
        ['C2', 'O2', 2], ['C6', 'O6', 2], ['N1', 'M1', 1], ['N3', 'M3', 1], ['N7', 'M7', 1]
      ],
      hex: [0, 0],
      pent: [1.554, 0]
    };

    function caffeine(b) {
      const { A, bonds } = CAF;
      const lab = 0.34, gp = HALO_PAD + 2;
      const minX = -1.732, maxX = 2.405, cx = (minX + maxX) / 2;
      const w = (maxX - minX + 2 * lab) * b + gp * 2, h = (4 + 2 * lab) * b + gp * 2;
      const P = k => [(A[k][0] - cx) * b, A[k][1] * b];
      const isLabel = k => k[0] === 'N' || k[0] === 'O';
      return makeSprite(w, h, w / 2, h / 2, g => inked(g, g => {
        g.strokeStyle = '#fff'; g.lineWidth = 1; g.lineCap = 'round'; g.lineJoin = 'round';
        g.beginPath();
        for (const [a, c, order, ring] of bonds) {
          const [x1, y1] = P(a), [x2, y2] = P(c);
          const L = Math.hypot(x2 - x1, y2 - y1), ux = (x2 - x1) / L, uy = (y2 - y1) / L;
          const s1 = isLabel(a) ? lab * b : 0, s2 = isLabel(c) ? lab * b : 0;
          const ax = x1 + ux * s1, ay = y1 + uy * s1, bx = x2 - ux * s2, by = y2 - uy * s2;
          if (order === 2 && !ring) {
            // C=O: two lines either side of the bond axis
            const ox = -uy * 0.09 * b, oy = ux * 0.09 * b;
            g.moveTo(ax + ox, ay + oy); g.lineTo(bx + ox, by + oy);
            g.moveTo(ax - ox, ay - oy); g.lineTo(bx - ox, by - oy);
            continue;
          }
          g.moveTo(ax, ay); g.lineTo(bx, by);
          if (order === 2) {
            // ring double bond: a shorter second line on the inside of its ring
            const rx = (CAF[ring][0] - cx) * b, ry = CAF[ring][1] * b;
            const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
            let nx = -uy, ny = ux;
            if ((rx - mx) * nx + (ry - my) * ny < 0) { nx = -nx; ny = -ny; }
            const off = 0.18 * b, t1 = Math.max(s1, 0.16 * b), t2 = Math.max(s2, 0.16 * b);
            g.moveTo(x1 + ux * t1 + nx * off, y1 + uy * t1 + ny * off);
            g.lineTo(x2 - ux * t2 + nx * off, y2 - uy * t2 + ny * off);
          }
        }
        g.stroke();
        // Atom labels are drawn as strokes, so they never wait on a font.
        g.beginPath();
        for (const k of Object.keys(A)) {
          if (!isLabel(k)) continue;
          const [x, y] = P(k);
          if (k[0] === 'N') {
            const hw = 0.15 * b, hh = 0.22 * b;
            g.moveTo(x - hw, y + hh); g.lineTo(x - hw, y - hh); g.lineTo(x + hw, y + hh); g.lineTo(x + hw, y - hh);
          } else {
            g.moveTo(x + 0.17 * b, y); g.ellipse(x, y, 0.17 * b, 0.22 * b, 0, 0, TAU);
          }
        }
        g.stroke();
      }));
    }

    function buildSprites() {
      const small = W < 600;
      if (spriteDPR === DPR && spriteSmall === small && S) return;
      spriteDPR = DPR;
      spriteSmall = small;
      const snow = [], dust = [], orn = [], beans = [];
      for (let i = 0; i < 10; i++) snow.push(crystal(lerp(10, 30, i / 9), 101 + i * 7));
      for (const s of [2, 2.6, 3.2, 4]) dust.push(softDot(s, 0.3));
      const shapes = ['plain', 'meridian', 'band', 'drop'];
      for (let i = 0; i < 8; i++) orn.push(glassOrnament(i >= 4 ? 11 : 7, shapes[i % 4]));
      for (let i = 0; i < 6; i++) beans.push(bean(lerp(9, 16, i / 5), 211 + i * 5));
      // Emblems alternate E mark and caffeine (even index = E, odd = caffeine); the second pair is larger.
      // Phones get them at about 80% so they stay small against the screen.
      const e = small ? [42, 9, 52, 11] : [50, 11, 62, 14];
      const emblem = [markOrnament(e[0]), caffeine(e[1]), markOrnament(e[2]), caffeine(e[3])];
      S = { snow, dust, orn, bean: beans, emblem, spark: softDot(3, 0.3) };
    }

    // ---------- particles ----------
    const VARIANTS = { snow: 10, dust: 4, orn: 8, bean: 6, emblem: 4 };
    const KINDS = Object.keys(VARIANTS);
    let emblemTurn = 0;

    function depthOf(kind, v) {
      if (kind === 'snow') return 0.35 + (0.65 * v) / 9;
      if (kind === 'dust') return 0.3 + (0.4 * v) / 3;
      if (kind === 'bean') return 0.45 + (0.55 * v) / 5;
      if (kind === 'emblem') return v < 2 ? 0.8 : 1;
      return v < 4 ? 0.6 : 1;
    }

    function newPart(kind, fresh) {
      const v = kind === 'emblem'
        ? (emblemTurn++ % 2) + 2 * Math.floor(R() * 2)
        : Math.floor(R() * VARIANTS[kind]);
      const z = depthOf(kind, v);
      const p = {
        kind, v, z,
        x: R() * W, y: 0,
        ph: R() * TAU, swayF: 0.35 + R() * 0.5, sway: 4 + R() * 14 * z,
        rot: R() * TAU, spin: (R() - 0.5) * 0.8, amp: 0.12 + R() * 0.22
      };
      const sp = S[kind][v];
      p.y = fresh ? R() * (H + sp.h) - sp.h : -sp.h - R() * 40;
      return p;
    }

    function fallSpeed(p) {
      if (p.kind === 'orn') return 10 + 16 * p.z;
      if (p.kind === 'emblem') return 9 + 10 * p.z;
      if (p.kind === 'dust') return 8 + 14 * p.z;
      if (p.kind === 'bean') return 14 + 24 * p.z;
      return 16 + 34 * p.z;
    }

    function alphaOf(p) {
      if (p.kind === 'snow') return 0.42 + 0.5 * p.z;
      if (p.kind === 'dust') return 0.3 + 0.4 * p.z;
      if (p.kind === 'bean') return 0.5 + 0.45 * p.z;
      if (p.kind === 'emblem') return 0.9;
      return 0.55 + 0.4 * p.z;
    }

    // Snow leads; ornaments and beans are accents; the emblems (E mark, caffeine) are rare.
    function targetCounts() {
      const n = Math.round(DENSITY_K[cfg.density] * Math.sqrt(W * H));
      const orn = Math.max(2, Math.round(n * 0.12));
      const bean = Math.max(1, Math.round(n * 0.12));
      const emblem = Math.max(2, Math.round(n * 0.05));
      const dust = Math.round(n * 0.2);
      return { snow: Math.max(0, n - orn - bean - emblem - dust), dust, orn, bean, emblem };
    }

    // fresh: new pieces appear anywhere on screen (first fill, density change); otherwise they enter from the top.
    function syncCount(fresh) {
      const want = targetCounts();
      for (const kind of KINDS) {
        const have = parts.filter(p => p.kind === kind);
        if (have.length > want[kind]) {
          const drop = new Set(have.slice(want[kind]));
          parts = parts.filter(p => !drop.has(p));
        } else {
          for (let i = have.length; i < want[kind]; i++) parts.push(newPart(kind, fresh));
        }
      }
      parts.sort((a, b) => a.z - b.z);
    }

    // ---------- reindeer ----------
    // Drawn facing right in a 100-unit box, origin at the middle of the body.
    const deer = { on: false, next: 1.6, x: 0, y: 0, y0: 0, vy: 0, dir: 1, speed: 60, w: 80, born: 0, emit: 0 };
    const deerCanvas = document.createElement('canvas');
    const dg = deerCanvas.getContext('2d');

    // Legs are jointed chains. Angles are degrees from straight down; positive swings toward the nose.
    // Front: forearm, cannon, pastern. Tucked, the knee points forward and the hoof folds back under the chest.
    // Hind: thigh, gaskin, cannon. Gathered, the hock points back; stretched, the leg trails out behind.
    const FRONT = { len: [8.5, 8, 2.6], wid: [3.6, 2.4, 2.0], tuck: [45, -60, -20], reach: [62, 80, 100] };
    const HIND = { len: [8.5, 7.5, 6.5], wid: [4.2, 2.8, 2.1], tuck: [20, -40, 10], reach: [-45, -80, -100] };

    function limb(spec, hip, c) {
      const pts = [hip];
      let x = hip[0], y = hip[1];
      for (let i = 0; i < spec.len.length; i++) {
        const a = lerp(spec.tuck[i], spec.reach[i], c) * D2R;
        x += Math.sin(a) * spec.len[i];
        y += Math.cos(a) * spec.len[i];
        pts.push([x, y]);
      }
      return { spec, pts };
    }

    // One gallop stride: all four legs gather under the body, then stretch out fore and aft together.
    // The far pair runs a beat behind the near pair.
    function legs(ph) {
      const c = 0.5 + 0.5 * Math.sin(ph);
      const cf = 0.5 + 0.5 * Math.sin(ph - 0.7);
      return [limb(FRONT, [13, 6], cf), limb(HIND, [-14, 4], cf), limb(FRONT, [16, 5], c), limb(HIND, [-17, 3], c)];
    }

    function bodyPath(g) {
      g.moveTo(-24, -3);
      g.bezierCurveTo(-23, -9, -16, -10.5, -8, -9.8);
      g.bezierCurveTo(2, -9.2, 10, -10.6, 16, -10.2);
      g.bezierCurveTo(24, -9.6, 27.5, -2.5, 24.5, 3.5);
      g.bezierCurveTo(21.5, 8.8, 12, 9.2, 4, 8.2);
      g.bezierCurveTo(-4, 7.4, -12, 8, -18, 6.8);
      g.bezierCurveTo(-24, 5.6, -26.5, 1.5, -24, -3);
      g.closePath();
    }

    function strokeLimb(g, leg, grow) {
      const { spec, pts } = leg;
      for (let i = 0; i < spec.wid.length; i++) {
        g.lineWidth = spec.wid[i] + grow * 2;
        g.beginPath(); g.moveTo(pts[i][0], pts[i][1]); g.lineTo(pts[i + 1][0], pts[i + 1][1]); g.stroke();
      }
    }

    function silhouette(g, L, grow) {
      const shape = build => {
        g.beginPath(); build();
        if (grow) { g.lineWidth = grow * 2; g.stroke(); }
        g.fill();
      };
      shape(() => g.ellipse(-25.4, -5.4, 3.5, 1.9, -0.8, 0, TAU));
      shape(() => bodyPath(g));
      shape(() => { g.moveTo(13, -9); g.lineTo(22, -21); g.lineTo(27, -18); g.lineTo(23, 1); g.closePath(); });
      shape(() => g.ellipse(23.2, -26.6, 4.6, 1.9, 0.5, 0, TAU));
      shape(() => g.ellipse(29.6, -22.4, 8.2, 6, -0.18, 0, TAU));
      shape(() => g.ellipse(37.2, -19.8, 6.6, 3.9, 0.14, 0, TAU));
      for (const leg of L) strokeLimb(g, leg, grow);
    }

    function antlers(g, lw) {
      g.strokeStyle = '#fff'; g.lineWidth = lw; g.lineCap = 'round';
      g.beginPath();
      g.moveTo(26.6, -27.6); g.quadraticCurveTo(22, -40, 15, -44);
      g.moveTo(23.1, -35.25); g.lineTo(28.1, -41.2);
      g.moveTo(19, -40.8); g.lineTo(19.3, -47.4);
      g.moveTo(30.8, -28); g.quadraticCurveTo(31.5, -40, 37, -46);
      g.moveTo(31.9, -35.7); g.lineTo(37.7, -37.8);
      g.moveTo(33.7, -41.1); g.lineTo(31.7, -47.5);
      g.stroke();
    }

    // Paint the silhouette fat, then cut the silhouette back out, leaving one hairline edge.
    function paintDeer(g, ph, u) {
      const L = legs(ph);
      const w = 1.15 / u;
      g.lineCap = 'round'; g.lineJoin = 'round';
      g.fillStyle = '#fff'; g.strokeStyle = '#fff';
      silhouette(g, L, w);
      g.globalCompositeOperation = 'destination-out';
      silhouette(g, L, 0);
      g.globalCompositeOperation = 'source-over';
      antlers(g, w);
      g.fillStyle = '#fff';
      g.beginPath(); g.arc(31.5, -24, 0.95, 0, TAU); g.fill();
      g.save();
      g.shadowColor = 'rgba(230, 70, 60, 0.7)';
      g.shadowBlur = 4 * DPR;
      g.fillStyle = '#d2463c';
      g.beginPath(); g.arc(43.7, -19.9, 2.3, 0, TAU); g.fill();
      g.restore();
    }

    function spawnDeer(t) {
      deer.on = true;
      deer.dir = R() < 0.5 ? 1 : -1;
      deer.w = clamp(W * 0.13, 66, 124);
      deer.x = deer.dir > 0 ? -deer.w : W + deer.w;
      deer.y0 = H * (0.2 + R() * 0.45);
      deer.speed = Math.max(60, W / 10);
      deer.born = t;
      deer.emit = 0;
    }

    function stepDeer(dt, t) {
      if (!deer.on) {
        if (t >= deer.next) spawnDeer(t);
        else return;
      }
      const k = t - deer.born;
      deer.x += deer.dir * deer.speed * dt;
      deer.y = deer.y0 + 16 * Math.sin(k * 1.5);
      deer.vy = 24 * Math.cos(k * 1.5);
      if ((deer.dir > 0 && deer.x > W + deer.w) || (deer.dir < 0 && deer.x < -deer.w)) {
        deer.on = false;
        deer.next = t + 14 + R() * 6;
        return;
      }
      deer.emit -= dt;
      const u = deer.w / 100;
      while (deer.emit <= 0) {
        deer.emit += 0.1;
        sparks.push({
          x: deer.x - deer.dir * 27 * u + (R() - 0.5) * 6 * u,
          y: deer.y + (R() - 0.3) * 8 * u,
          vx: -deer.dir * (8 + R() * 14), vy: 6 + R() * 18,
          age: 0, life: 0.9 + R() * 0.8, tw: R() * TAU, s: 0.6 + R() * 0.7
        });
      }
    }

    function stepSparks(dt) {
      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i];
        s.age += dt;
        if (s.age >= s.life) { sparks.splice(i, 1); continue; }
        s.x += s.vx * dt; s.y += s.vy * dt;
      }
    }

    function drawDeer(t) {
      if (!deer.on) return;
      const u = deer.w / 100, k = t - deer.born, ph = k * 5.5;
      const cw = Math.ceil(104 * u * DPR), ch = Math.ceil(84 * u * DPR);
      if (deerCanvas.width !== cw || deerCanvas.height !== ch) { deerCanvas.width = cw; deerCanvas.height = ch; }
      dg.setTransform(1, 0, 0, 1, 0, 0);
      dg.globalCompositeOperation = 'source-over';
      dg.clearRect(0, 0, cw, ch);
      dg.setTransform(u * DPR, 0, 0, u * DPR, 48 * u * DPR, 55 * u * DPR);
      paintDeer(dg, ph, u);
      // Pitch follows the flight path, plus a slight rock with each stride (nose up as the legs gather).
      const a = deer.dir * (Math.atan2(deer.vy, deer.speed) * 0.7 + 0.05 * Math.sin(ph));
      const c = Math.cos(a) * DPR, s = Math.sin(a) * DPR;
      ctx.globalAlpha = 0.95;
      ctx.shadowColor = DEER_GLOW;
      ctx.shadowBlur = DEER_GLOW_BLUR * DPR;
      ctx.setTransform(c * deer.dir, s * deer.dir, -s, c, deer.x * DPR, deer.y * DPR);
      ctx.drawImage(deerCanvas, -48 * u, -55 * u, cw / DPR, ch / DPR);
      ctx.shadowBlur = 0;
      ctx.shadowColor = 'transparent';
    }

    function drawSparks() {
      const sp = S.spark;
      for (const s of sparks) {
        const k = 1 - s.age / s.life;
        ctx.globalAlpha = k * (0.55 + 0.45 * Math.sin(s.age * 18 + s.tw)) * 0.7;
        ctx.setTransform(DPR * s.s, 0, 0, DPR * s.s, s.x * DPR, s.y * DPR);
        ctx.drawImage(sp.c, -sp.ox, -sp.oy, sp.w, sp.h);
      }
    }

    // ---------- loop ----------
    function put(x, y, a) {
      const c = Math.cos(a) * DPR, s = Math.sin(a) * DPR;
      ctx.setTransform(c, s, -s, c, x * DPR, y * DPR);
    }

    function step(dt, t) {
      const wind = 6 + 10 * Math.sin(t * 0.05);
      // Desktop speed is right; a phone screen at the same speed reads as rushed, so narrow screens fall at ~65%.
      const sc = clamp(H / 900, 0.7, 1.1) * lerp(0.65, 1, clamp((W - 390) / 634, 0, 1));
      for (const p of parts) {
        const sp = S[p.kind][p.v];
        p.y += fallSpeed(p) * sc * dt;
        p.x += wind * (0.3 + 0.7 * p.z) * dt;
        if (p.y - sp.h > H) { p.y = -sp.h - R() * 30; p.x = R() * W; }
        if (p.x > W + 40) p.x -= W + 80;
        else if (p.x < -40) p.x += W + 80;
      }
      stepDeer(dt, t);
      stepSparks(dt);
    }

    function draw(t) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = 1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of parts) {
        const sp = S[p.kind][p.v];
        ctx.globalAlpha = alphaOf(p);
        if (p.kind === 'orn' || p.kind === 'emblem') put(p.x, p.y, p.amp * Math.sin(t * p.swayF * 1.6 + p.ph));
        else if (p.kind === 'dust') put(p.x + p.sway * 0.6 * Math.sin(t * p.swayF + p.ph), p.y, 0);
        else put(p.x + p.sway * Math.sin(t * p.swayF + p.ph), p.y, p.rot + p.spin * t);
        ctx.drawImage(sp.c, -sp.ox, -sp.oy, sp.w, sp.h);
      }
      drawSparks();
      drawDeer(t);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = 1;
    }

    // The theme locks scrolling with an overflow-hidden class while the menu or cart drawer is open.
    // Those drawers blur what's behind them, so the art holds still rather than force a re-blur every frame.
    const drawerOpen = () => root.classList.contains('overflow-hidden') || document.body.classList.contains('overflow-hidden');

    function frame(time) {
      raf = requestAnimationFrame(frame);
      if (drawerOpen()) { last = time; return; }
      if (time - lastFrame < 14) return; // about 60 fps at most, even on 120 Hz screens
      lastFrame = time;
      const dt = clamp((time - last) / 1000, 0, 0.05);
      last = time;
      clock += dt;
      step(dt, clock);
      draw(clock);
      frames++;
    }

    // Motion always runs, by Trent's call (it does not follow prefers-reduced-motion).
    // It pauses only while the tab is hidden or a drawer is open.
    function start() {
      if (raf || dead || !booted) return;
      last = lastFrame = performance.now();
      raf = requestAnimationFrame(frame);
    }

    function stop() {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    }

    // The phone address bar showing and hiding changes the height on most scrolls. The canvas follows it,
    // but the piece count only changes with the width or a big height change, so nothing pops in or out.
    function resize() {
      DPR = Math.min(window.devicePixelRatio || 1, 2);
      W = window.innerWidth; H = window.innerHeight;
      canvas.width = Math.round(W * DPR);
      canvas.height = Math.round(H * DPR);
      buildSprites();
      const first = !lastW;
      if (first || W !== lastW || Math.abs(H - lastH) > lastH * 0.25) {
        syncCount(first);
        lastW = W; lastH = H;
      }
      draw(clock);
    }

    let resizeQueued = false;
    function onResize() {
      if (resizeQueued || !booted) return;
      resizeQueued = true;
      requestAnimationFrame(() => { if (resizeQueued) { resizeQueued = false; resize(); } });
      setTimeout(() => { if (resizeQueued) { resizeQueued = false; resize(); } }, 250);
    }
    function onVisibility() { if (document.hidden) stop(); else start(); }

    // Moving the window to a screen with a different pixel density fires no resize; watch for it directly.
    let dprQuery = null;
    function onDpr() { if (booted) resize(); watchDpr(); }
    function watchDpr() {
      if (dprQuery) dprQuery.removeEventListener('change', onDpr);
      dprQuery = window.matchMedia ? matchMedia('(resolution: ' + (window.devicePixelRatio || 1) + 'dppx)') : null;
      if (dprQuery && dprQuery.addEventListener) dprQuery.addEventListener('change', onDpr);
    }

    // Sprites are built when the browser is idle, so the page's own first paint never waits on them.
    function boot() {
      if (booted || dead) return;
      booted = true;
      resize();
      watchDpr();
      if (!document.hidden) start();
    }
    const idle = window.requestIdleCallback ? requestIdleCallback(boot, { timeout: 1500 }) : setTimeout(boot, 200);

    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibility);

    function destroy() {
      dead = true;
      stop();
      if (window.cancelIdleCallback && window.requestIdleCallback) cancelIdleCallback(idle); else clearTimeout(idle);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
      if (dprQuery && dprQuery.removeEventListener) dprQuery.removeEventListener('change', onDpr);
      canvas.remove();
      style.remove();
      root.classList.remove('ec-festive-on', 'ec-festive-behind', 'ec-festive-unpin');
    }

    // Controls for the vault prototype, plus hooks for checking renders without waiting on frames.
    canvas.ecFestive = {
      setDensity(d) { if (DENSITY_K[d]) { cfg.density = d; if (booted) { syncCount(true); draw(clock); } } },
      setLayer: applyLayer,
      soonDeer(sec) { if (!deer.on) deer.next = Math.min(deer.next, clock + sec); },
      frames: () => frames,
      count: () => parts.length,
      deer: () => ({ on: deer.on, x: Math.round(deer.x), y: Math.round(deer.y), w: Math.round(deer.w) }),
      offsetShadow: OFFSET_SHADOW,
      advance(sec) { boot(); for (let s = 0; s < sec; s += 1 / 60) { clock += 1 / 60; step(1 / 60, clock); } draw(clock); },
      drawCost(n = 60) { boot(); const t0 = performance.now(); for (let i = 0; i < n; i++) draw(clock); return (performance.now() - t0) / n; },
      sprite: (kind, v) => { boot(); return S[kind][v]; },
      specimen(cnv, ph, scale = 4) {
        const g = cnv.getContext('2d');
        cnv.width = Math.ceil(104 * scale * DPR); cnv.height = Math.ceil(84 * scale * DPR);
        g.setTransform(scale * DPR, 0, 0, scale * DPR, 48 * scale * DPR, 55 * scale * DPR);
        paintDeer(g, ph, scale);
      }
    };

    return { sectionId, destroy };
  }
})();
